import { useEffect, useState } from "react";
import api from "../lib/api";

export default function Reports() {
  const [period, setPeriod] = useState(30);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let active = true;
    setError("");
    setData(null);
    setLoading(true);
    api.get("/reporting/summary", { params: { days: period } })
      .then(({ data: result }) => { if (active) setData(result); })
      .catch(() => { if (active) setError("Could not load reporting data."); })
      .finally(() => { if (active) setLoading(false); });
  return () => { active = false; };
  }, [period]);
    async function exportFile(format) {
    try {
      const response = await api.get(`/reporting/export.${format}`, { params: { days: period }, responseType: "blob" });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `monitorflux-report.${format}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch { setError("Could not export report. Please retry."); }
  }
  return (
    <main className="mx-auto w-full min-w-0 space-y-6 sm:space-y-8">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div><h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Monitoring reports</h1>
          <p className="mt-1 text-sm text-slate-500">Review availability and incidents across your webapps.</p></div>
        <label className="flex items-center gap-2 text-sm text-slate-600">Period
          <select className="min-h-11 rounded-xl border border-slate-300 bg-white px-4 py-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-600" value={period} onChange={e => setPeriod(Number(e.target.value))}>
            <option value={7}>7 days</option><option value={30}>30 days</option><option value={90}>90 days</option>
          </select>
        </label>
      </div>
      <div className="flex flex-wrap gap-3"><button className="rounded-lg bg-blue-600 text-white px-4 py-2 text-sm disabled:opacity-50" disabled={!data} onClick={()=>exportFile("csv")}>Export CSV</button><button className="rounded-lg border px-4 py-2 text-sm disabled:opacity-50" disabled={!data} onClick={()=>exportFile("pdf")}>Export PDF</button><p className="text-xs text-slate-500 self-center">{data ? `${new Date(data.start).toLocaleDateString(undefined, { timeZone: "UTC" })} – ${new Date(data.end).toLocaleDateString(undefined, { timeZone: "UTC" })} · UTC` : ""}</p></div>
      <div className="om-premium-card overflow-hidden">
        <div className="space-y-3 p-3 md:hidden">
          {(data?.webapps || []).map(w => (
            <article key={w.id} className="rounded-xl border border-slate-200 bg-slate-50/60 p-4">
              <h2 className="truncate text-sm font-semibold text-slate-900">{w.name}</h2>
              <p className="mt-1 truncate text-xs text-slate-500">{w.url}</p>
              <dl className="mt-4 grid grid-cols-2 gap-3">
                <div><dt className="text-xs text-slate-500">Checks</dt><dd className="mt-1 text-base font-semibold text-slate-900">{w.checks}</dd></div>
                <div><dt className="text-xs text-slate-500">Sample uptime</dt><dd className="mt-1 text-base font-semibold text-slate-900">{w.uptime_sample_percent == null ? "—" : w.uptime_sample_percent + "%"}</dd></div>
                <div><dt className="text-xs text-slate-500">Incidents</dt><dd className="mt-1 text-base font-semibold text-slate-900">{w.incidents}</dd></div>
                <div><dt className="text-xs text-slate-500">Security signals</dt><dd className="mt-1 text-base font-semibold text-slate-900">{w.security_findings?.length || 0}</dd></div>
              </dl>
            </article>
          ))}
        </div>
        <div className="hidden overflow-x-auto md:block">
          <table className="min-w-[650px] w-full text-left text-sm">
            <thead className="bg-slate-50 text-slate-600"><tr>
              <th className="p-4">Webapp</th><th className="p-4">Checks</th>
              <th className="p-4">Sample uptime</th><th className="p-4">Coverage / downtime</th><th className="p-4">Incidents</th><th className="p-4">Security checks</th>
            </tr></thead>
            <tbody>{(data?.webapps || []).map(w => <tr key={w.id} className="border-t border-slate-100">
              <td className="p-4"><div className="font-medium text-slate-800">{w.name}</div><div className="max-w-[300px] truncate text-xs text-slate-500">{w.url}</div></td>
              <td className="p-4">{w.checks}</td>
              <td className="p-4">{w.uptime_sample_percent == null ? "Insufficient data" : w.uptime_sample_percent + "%"}</td>
              <td className="p-4">{w.coverage_percent}% observed<br/><span className="text-xs text-slate-500">{Math.round(w.downtime_seconds / 60)} min down · {w.sla_status.replaceAll("_", " ")}</span></td><td className="p-4">{w.incidents}</td><td className="p-4">{w.security_findings?.length || 0} findings{w.security_findings?.length > 0 && <details className="mt-2"><summary className="cursor-pointer text-blue-600">Review</summary><ul className="mt-2 space-y-1">{w.security_findings.map((f, i) => <li key={i} className="text-xs text-slate-600">{f.source}: {f.finding}</li>)}</ul></details>}</td>
            </tr>)}</tbody>
          </table>
        </div>
        {loading && !error && <p className="p-6 text-sm text-slate-500" role="status">Loading report...</p>}
        {error && <p role="alert" className="p-6 text-sm text-red-600">{error}</p>}
        {data?.webapps?.length === 0 && <p className="p-6 text-sm text-slate-500">No monitored webapps yet.</p>}
      </div>
      <p className="text-xs text-slate-500">{data?.note || "Reports distinguish observed availability from unknown gaps."}</p>
    </main>
  );
}
